/**
 * Uma pergunta na tela: o controle do tipo dela, mais tudo o que o servidor decidiu sobre ela.
 *
 * O que fica em volta do controle não é enfeite — é o que o inventário mostrou faltar:
 *
 * · **por que esta pergunta apareceu.** Onze ramificações do formulário atual vivem em prosa
 *   dentro do enunciado ("Caso tenha respondido sim..."), e o campo aparece para todo mundo do
 *   mesmo jeito. Aqui a condição é dado, e a frase que a explica vem do servidor.
 *
 * · **de onde veio a resposta pré-preenchida.** Clientes já pedem herança escrevendo "igual ao
 *   força de vendas". A sugestão aparece marcada e **não está gravada** até alguém confirmar:
 *   confirmar é salvar o mesmo valor, e é o que separa procedência observada de declaração.
 *
 * · **o arquivo desta pergunta**, e não de um balde da trilha.
 *
 * Dois tipos não passam pelo mapa de controles, e por motivos opostos: `bloco_informativo` não
 * tem valor nenhum para gravar, e `credencial` não grava o valor AQUI — ele vai cifrado para o
 * cofre e a resposta guarda só a referência ao segredo. Os dois saem do `Record` de propósito:
 * é o `Exclude` que faz o compilador cobrar um controle para cada tipo restante, e um tipo novo
 * do catálogo continua sem compilar até alguém decidir como ele aparece.
 */

import type { ReactElement } from "react";
import { Aviso, Botao, Chip } from "../ui";
import { classes } from "../../nucleo/classes";
import { AnexosDaPergunta } from "./AnexosDaPergunta";
import { CampoDeCredencial } from "./CampoDeCredencial";
import { DominioFixoDaInfra } from "./DominioFixoDaInfra";
import { ancoraDaPergunta } from "./ancora";
import {
  ControleDeContato,
  ControleDeCor,
  ControleDeEscolha,
  ControleDeGaleria,
  ControleDeLista,
  ControleDeTexto,
  ControleDeTextoLongo,
  ControleDeUpload,
  ControleNumerico,
  ControleSimNao,
  type PropsDoControle,
} from "./controles";
import type { PerguntaDoFormulario, ValorDaPergunta } from "../../nucleo/kickoff";

export interface PropsDaPergunta {
  pergunta: PerguntaDoFormulario;
  valor: ValorDaPergunta | null;
  erro?: string | undefined;
  /** A recusa do último anexo desta pergunta, para aparecer onde o clique aconteceu. */
  falhaDoAnexo?: string | undefined;
  somenteLeitura: boolean;
  podeAnexar?: boolean;
  /** Alguma escrita em andamento — anexo subindo, trilha sendo enviada. */
  ocupado: boolean;
  /** Nome legível da trilha de onde veio a sugestão herdada. */
  nomeDaOrigem?: string | undefined;
  aoMudar: (valor: ValorDaPergunta | null) => void;
  aoAnexar: (arquivo: File) => void;
  aoRemoverAnexo: (anexoId: string) => void;
}

const CONTROLES: Record<
  Exclude<PerguntaDoFormulario["tipo"], "bloco_informativo" | "credencial">,
  (props: PropsDoControle) => ReactElement
> = {
  texto_curto: ControleDeTexto,
  outro: ControleDeTexto,
  url: ControleDeTexto,
  fonte: ControleDeTexto,
  texto_longo: ControleDeTextoLongo,
  upload: ControleDeUpload,
  numero: ControleNumerico,
  cor: ControleDeCor,
  sim_nao: ControleSimNao,
  escolha_unica: ControleDeEscolha,
  escolha_multipla: ControleDeEscolha,
  galeria: ControleDeGaleria,
  contato: ControleDeContato,
  lista_repetivel: ControleDeLista,
};

export function PerguntaNoFormulario({
  pergunta,
  valor,
  erro,
  falhaDoAnexo,
  somenteLeitura,
  podeAnexar = !somenteLeitura,
  ocupado,
  nomeDaOrigem,
  aoMudar,
  aoAnexar,
  aoRemoverAnexo,
}: PropsDaPergunta) {
  // Pergunta escondida por condicional não é desenhada apagada: ela não se aplica a este
  // cliente, e mostrá-la em cinza convidaria a responder o que o servidor vai ignorar.
  if (!pergunta.visivel) return null;

  if (pergunta.tipo === "bloco_informativo") {
    const ajuda = pergunta.ajuda?.startsWith(pergunta.enunciado)
      ? pergunta.ajuda.slice(pergunta.enunciado.length).replace(/^[\s.:–-]+/, "")
      : pergunta.ajuda;
    return (
      <section
        className="pergunta pergunta--informativa"
        id={ancoraDaPergunta(pergunta.id)}
        data-testid={`pergunta-${pergunta.id}`}
        data-tipo={pergunta.tipo}
      >
        <Aviso tom="marca">
          <b>{pergunta.enunciado}</b>
          {ajuda ? <p className="cartao__texto mt-1">{ajuda}</p> : null}
          {pergunta.config.orientacao ? <p>{pergunta.config.orientacao}</p> : null}
          {pergunta.config.links?.map((link) => (
            /^https?:\/\//.test(link.url) ? <p key={link.url}><a href={link.url} target="_blank" rel="noreferrer">{link.rotulo}</a></p> : null
          ))}
          {pergunta.config.tabela ? (
            <div className="overflow-x-auto"><table className="tabela">
              <caption>{pergunta.enunciado}</caption>
              <thead><tr>{pergunta.config.tabela.colunas.map((coluna) => <th scope="col" key={coluna}>{coluna}</th>)}</tr></thead>
              <tbody>{pergunta.config.tabela.linhas.map((linha, i) => (
                <tr key={i}>{linha.map((celula, j) => <td key={j}>{celula}</td>)}</tr>
              ))}</tbody>
            </table></div>
          ) : null}
        </Aviso>
      </section>
    );
  }

  /* `credencial` não está no mapa: ele não é um controle que traduz envelope, é um campo que
     fala com outro serviço. Escolher aqui, e não lá dentro, mantém o mapa com a única
     responsabilidade que o arquivo `controles.tsx` declara ter. */
  const Controle =
    pergunta.tipo === "credencial" ? CampoDeCredencial
      : pergunta.id === "infra.url_retaguarda" ? DominioFixoDaInfra
      : CONTROLES[pergunta.tipo];
  const resposta = pergunta.resposta;
  const sugerida = resposta?.sugerida === true;
  const herdada = resposta !== null && resposta.herdada_de_pergunta_id !== null && !sugerida;
  const origem = nomeDaOrigem ?? resposta?.herdada_de_trilha ?? "outra trilha";

  return (
    <section
      className={classes("pergunta", erro && "pergunta--com-erro")}
      id={ancoraDaPergunta(pergunta.id)}
      data-testid={`pergunta-${pergunta.id}`}
      data-tipo={pergunta.tipo}
      data-obrigatoria={pergunta.obrigatoria ? "sim" : "nao"}
      data-herdada={sugerida ? "sugerida" : herdada ? "confirmada" : undefined}
    >
      {sugerida ? (
        <Aviso tom="marca" className="mb-3" data-testid={`heranca-${pergunta.id}`}>
          <b>Resposta trazida de {origem}.</b> Ela ainda <b>não está salva</b> — confira, ajuste
          se precisar e confirme.{" "}
          {somenteLeitura ? null : (
            <Botao
              variante="fantasma"
              tamanho="pequeno"
              className="mt-2"
              disabled={ocupado}
              onClick={() => aoMudar(resposta.valor)}
            >
              Usar esta resposta
            </Botao>
          )}
        </Aviso>
      ) : null}

      {herdada ? (
        <p className="pergunta__selos">
          <Chip estado="ok" data-testid={`herdada-${pergunta.id}`}>
            herdada de {origem}
          </Chip>
        </p>
      ) : null}

      <Controle
        pergunta={pergunta}
        valor={valor}
        erro={erro}
        desabilitado={somenteLeitura}
        aoMudar={aoMudar}
      />

      {pergunta.config.orientacao ? <p className="campo__ajuda">{pergunta.config.orientacao}</p> : null}

      {/* Só o caso INDETERMINADO fala. A pergunta que apareceu porque a pessoa respondeu "sim"
          não precisa de legenda: ela acabou de responder e viu o bloco surgir. Repetir isso sob
          cada uma das sete perguntas reveladas enchia a tela de texto redundante justamente onde
          o portal mostra o seu melhor recurso. Ver o 3.2 dos ajustes da fase 1. */}
      {pergunta.condicional && pergunta.condicional.satisfeita === null ? (
        <p className="pergunta__condicional" data-testid={`condicional-${pergunta.id}`}>
          Não temos como confirmar aqui se isto se aplica a você ({
            pergunta.condicional.explicacao
          }), então a pergunta fica visível e <b>não é obrigatória</b>.
        </p>
      ) : null}

      {pergunta.aceita_anexo ? (
        <AnexosDaPergunta
          pergunta={pergunta}
          desabilitado={!podeAnexar}
          podeRemover={!somenteLeitura}
          ocupado={ocupado}
          falha={falhaDoAnexo}
          aoAnexar={aoAnexar}
          aoRemover={aoRemoverAnexo}
        />
      ) : null}
    </section>
  );
}
